import React, { useMemo, useCallback, useState, memo } from "react";
import { makeStyles } from "@material-ui/styles";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import CheckBoxOutlinedIcon from "@material-ui/icons/CheckBoxOutlined";
import {
  createLogEntryPayload,
  withTrackProps,
} from "@dpdgroupuk/react-event-tracker";

import RightDrawerModal from "../../RightDrawerModal";
import {
  NEW_MERGE,
  APPLY_MERGE,
  SELECTED_STOPS,
  MERGE_TO,
  SHOW_MATCHING_POSTCODES,
  RESULT,
  NO_VALID_STOPS_AVAILABLE,
} from "../../../constants/strings";
import palette from "../../styles/palette";
import Button from "../../Button";
import { getDropdownListStops } from "../../../features/routeDetail/model";
import SliderCard from "../../SliderCard";
import NestedSelect from "../../NestedSelect";
import { findStopByStopNumber } from "../../../models/stop";
import ModalTable from "../../common/Table/ModalTable";
import stopListColumns from "../../RouteDetail/StopList/stopListColumns";
import { mergeStopsColumnsConfigs } from "../../../enums/columnsConfigs";
import {
  headerCellModalStyles,
  bodyCellModalStyles,
} from "../../styles/tablesCells";
import {
  createStopOptions,
  getMergeSplitTableData,
} from "../../../models/editStop";
import { MERGE_STOPS } from "../../../constants/analytics";

const useStyles = makeStyles(({ typography }) => ({
  button: {
    border: "1px solid #0060A2",
    "&:disabled": {
      opacity: 0.38,
    },
  },
  label: {
    marginLeft: 0,
    marginBottom: typography.pxToRem(19),
  },
  checkbox: {
    padding: 0,
    marginRight: typography.pxToRem(13),
    color: palette.typography.black.solid,
    "& svg": {
      fontSize: `${typography.pxToRem(18)}`,
    },
  },
}));

const MergeStopsModal = ({
  open,
  onClose,
  onConfirm,
  handleMergeStopPick,
  availableStops,
  selectedStops,
  leadStop,
  toggleShouldMatch,
  handleSelectMergeToStop,
}) => {
  const classes = useStyles();
  const headerCellClasses = headerCellModalStyles();
  const bodyCellClasses = bodyCellModalStyles();
  const [shouldMatchPostcodes, toggleShouldMatchPostcodes] = useState(true);
  const [selectedStop, setSelectedStop] = useState(null);
  const [mergeStop, setMergeStop] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const dropdownStopsOptions = useMemo(
    () =>
      createStopOptions(
        getDropdownListStops(leadStop, availableStops, shouldMatchPostcodes)
      ),
    [availableStops, leadStop, shouldMatchPostcodes]
  );

  const onChangeShouldMatchPostcodes = useCallback(() => {
    toggleShouldMatch();
    toggleShouldMatchPostcodes(!shouldMatchPostcodes);
  }, [toggleShouldMatchPostcodes, shouldMatchPostcodes, toggleShouldMatch]);

  const onSelectMergeToStop = useCallback(
    async (selectedStopNumber) => {
      handleSelectMergeToStop(selectedStopNumber);
      setLoading(true);
      setSelectedStop(selectedStopNumber);

      const stop = findStopByStopNumber(availableStops, selectedStopNumber);

      const mergeStop = await handleMergeStopPick(stop);
      setLoading(false);

      if (mergeStop) {
        setMergeStop({
          ...stop,
          ...mergeStop,
        });
      }
    },
    [availableStops, handleMergeStopPick, handleSelectMergeToStop]
  );

  const confirmMerge = useCallback(() => onConfirm(mergeStop), [
    mergeStop,
    onConfirm,
  ]);

  const {
    tableColumns: topTableColumns,
    stopsData: topTableStopsData,
  } = useMemo(
    () => ({
      tableColumns: stopListColumns({
        headerCellClasses,
        bodyCellClasses,
        columnsConfigs: mergeStopsColumnsConfigs,
      }),
      stopsData: getMergeSplitTableData(selectedStops),
    }),
    [bodyCellClasses, headerCellClasses, selectedStops]
  );

  const bottomTableColumns = useMemo(
    () =>
      stopListColumns({
        headerCellClasses,
        bodyCellClasses,
        columnsConfigs: mergeStopsColumnsConfigs,
      }),
    [bodyCellClasses, headerCellClasses]
  );
  const bottomTableStopsData = useMemo(
    () => (mergeStop ? getMergeSplitTableData([mergeStop]) : {}),
    [mergeStop]
  );

  return (
    <RightDrawerModal
      open={open}
      onClose={onClose}
      title={NEW_MERGE.toUpperCase()}
      loadId={MERGE_STOPS.LOAD}
      actions={
        <Button
          data-test-id="apply merge"
          variant="contained"
          disabled={!mergeStop || isLoading}
          bgColor={palette.modalButtons.blue}
          onClick={confirmMerge}
          className={classes.button}
        >
          {APPLY_MERGE}
        </Button>
      }
    >
      <ModalTable
        title={SELECTED_STOPS}
        columns={topTableColumns}
        data={topTableStopsData}
      />
      <SliderCard title={MERGE_TO}>
        <FormControlLabel
          label={SHOW_MATCHING_POSTCODES}
          className={classes.label}
          control={
            <Checkbox
              onChange={onChangeShouldMatchPostcodes}
              checked={shouldMatchPostcodes}
              color="default"
              disableRipple
              className={classes.checkbox}
              checkedIcon={<CheckBoxOutlinedIcon />}
            />
          }
        />
        <NestedSelect
          value={selectedStop}
          onChange={onSelectMergeToStop}
          options={dropdownStopsOptions}
          label={
            dropdownStopsOptions.length ? MERGE_TO : NO_VALID_STOPS_AVAILABLE
          }
          disabled={!dropdownStopsOptions.length}
        />
      </SliderCard>
      {mergeStop && (
        <ModalTable
          title={RESULT}
          columns={bottomTableColumns}
          data={bottomTableStopsData}
        />
      )}
    </RightDrawerModal>
  );
};

export default withTrackProps({
  onConfirm: MERGE_STOPS.CLICK_APPLY_MERGE,
  onClose: MERGE_STOPS.CLICK_CLOSE,
  toggleShouldMatch: MERGE_STOPS.SELECT_MATCHING_POSTCODES,
  handleSelectMergeToStop: (value) =>
    createLogEntryPayload(MERGE_STOPS.SELECT_MERGE_TO_STOP, { value }),
})(memo(MergeStopsModal));
